/**
 * My Service Proposals Hooks
 *
 * Provider-facing half of ticket 86eya6nqh: a provider proposes a service, Operations
 * approves or rejects it in the internal dashboard. Approval auto-activates — the backend
 * creates (or reuses) a ServiceTemplate and adds it to the provider's capable_services.
 *
 * Read:  GraphQL myServiceProposals(limit: Int) — caller-scoped, returns only the
 *        signed-in provider's own proposals.
 * Write: POST /api/providers/service-proposals/
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { graphqlQuery } from '@/services/graphql'
import { submitServiceProposal, toProviderServiceError } from '@/api/providerServices'
import { useLocalization } from '@/contexts/LocalizationContext'
import type { ServiceProposal, SubmitServiceProposalInput } from '@/types/providerServices'

const MY_SERVICE_PROPOSALS_QUERY = `
  query MyServiceProposals($limit: Int) {
    myServiceProposals(limit: $limit) {
      id
      serviceName
      description
      status
      statusDisplay
      rejectionReason
      reviewedAt
      createdAt
      proposedPrice
      estimatedDurationSeconds
      providerId
      categoryName
      resultingServiceTemplateId
    }
  }
`

interface MyServiceProposalsResponse {
  myServiceProposals: ServiceProposal[] | null
}

const DEFAULT_LIMIT = 50

export function useMyServiceProposals(enabled = true) {
  const query = useQuery({
    queryKey: ['myServiceProposals'],
    queryFn: async () => {
      const response = await graphqlQuery<MyServiceProposalsResponse>(
        MY_SERVICE_PROPOSALS_QUERY,
        { limit: DEFAULT_LIMIT }
      )
      return response.data?.myServiceProposals ?? []
    },
    enabled,
    staleTime: 1000 * 30,
  })

  return {
    proposals: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/** Mutation for submitting a new service proposal. */
export function useServiceProposalActions() {
  const queryClient = useQueryClient()
  const { t } = useLocalization()

  const submitMutation = useMutation({
    mutationFn: async (input: SubmitServiceProposalInput) => {
      try {
        const res = await submitServiceProposal(input)
        if (res?.success === false) {
          throw new Error(t('myServices.submitFailed'))
        }
        return res
      } catch (err) {
        throw toProviderServiceError(err)
      }
    },
    onSuccess: async () => {
      toast.success(t('myServices.submitSuccess'))
      await queryClient.invalidateQueries({ queryKey: ['myServiceProposals'] })
    },
    onError: (err: Error) => {
      toast.error(t('myServices.submitFailed'), { description: err?.message })
    },
  })

  return {
    submitProposal: (input: SubmitServiceProposalInput) =>
      submitMutation.mutateAsync(input).then(() => {}),
    submitMutation,
    isLoading: submitMutation.isPending,
  }
}
